import { type BaseTicketComment } from "@mg/schemas/src/christo/catalyst";
import { Prohibit, Warning } from "@phosphor-icons/react";
import { type RefObject, useRef, useState } from "react";

interface ImageCommentPinProps {
  comment: BaseTicketComment;
  isActive: boolean;
  onPinClick: (comment: BaseTicketComment) => void;
  onDragEnd?: (
    comment: BaseTicketComment,
    newRelX: number,
    newRelY: number,
  ) => void;
  containerRef?: RefObject<HTMLDivElement | null>;
}

const DRAG_THRESHOLD_PX = 3;

export function ImageCommentPin({
  comment,
  isActive,
  onPinClick,
  onDragEnd,
  containerRef,
}: ImageCommentPinProps) {
  const [dragOffset, setDragOffset] = useState<{
    dx: number;
    dy: number;
  } | null>(null);
  const dragOffsetRef = useRef<{ dx: number; dy: number } | null>(null);
  const dragStartRef = useRef<{
    px: number;
    py: number;
    relX: number;
    relY: number;
  } | null>(null);

  const relX = comment.rel_x;
  const relY = comment.rel_y;
  if (relX == null || relY == null) return null;

  const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragStartRef.current = { px: e.clientX, py: e.clientY, relX, relY };
    dragOffsetRef.current = null;
    setDragOffset(null);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragStartRef.current || !containerRef?.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const dx = (e.clientX - dragStartRef.current.px) / rect.width;
    const dy = (e.clientY - dragStartRef.current.py) / rect.height;
    const offset = { dx, dy };
    dragOffsetRef.current = offset;
    setDragOffset(offset);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragStartRef.current) return;
    const totalPx =
      Math.abs(e.clientX - dragStartRef.current.px) +
      Math.abs(e.clientY - dragStartRef.current.py);

    if (totalPx > DRAG_THRESHOLD_PX && onDragEnd && containerRef?.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const dx = (e.clientX - dragStartRef.current.px) / rect.width;
      const dy = (e.clientY - dragStartRef.current.py) / rect.height;
      const newRelX = Math.max(0, Math.min(1, dragStartRef.current.relX + dx));
      const newRelY = Math.max(0, Math.min(1, dragStartRef.current.relY + dy));
      onDragEnd(comment, newRelX, newRelY);
    } else {
      onPinClick(comment);
    }
    dragStartRef.current = null;
    dragOffsetRef.current = null;
    setDragOffset(null);
  };

  const handlePointerCancel = () => {
    dragStartRef.current = null;
    dragOffsetRef.current = null;
    setDragOffset(null);
  };

  const displayX = dragOffset ? relX + dragOffset.dx : relX;
  const displayY = dragOffset ? relY + dragOffset.dy : relY;
  const isDragging = dragOffset != null;

  const Icon = comment.isRequired ? Prohibit : Warning;
  const innerBg = comment.isRequired ? "bg-puntt-red-9" : "bg-puntt-gold-10";

  return (
    <button
      type="button"
      data-testid="comment-pin"
      data-comment-id={comment._id}
      data-required={comment.isRequired ?? false}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      className="absolute z-20 cursor-grab touch-none border-none bg-transparent p-0"
      style={{
        left: `${displayX * 100}%`,
        top: `${displayY * 100}%`,
        cursor: isDragging ? "grabbing" : "grab",
      }}
      title={comment.description?.slice(0, 80)}
    >
      <div
        className={`rounded-t-2xl rounded-br-2xl p-0.5 shadow-md transition-all duration-150 ${
          isActive
            ? "bg-puntt-accent-9 ring-2 ring-puntt-accent-9/50 ring-offset-1"
            : "bg-puntt-red-9"
        }`}
        style={{
          transform: `translateY(-100%) rotate(-45deg) ${isActive ? "scale(1.35)" : "scale(1)"}`,
          transformOrigin: "0% 100%",
        }}
      >
        <div
          className={`flex size-6 items-center justify-center rounded-full ${
            isActive ? "bg-puntt-accent-9" : innerBg
          }`}
          style={{ transform: "rotate(45deg)" }}
        >
          <Icon size={14} weight="fill" className="text-base-white" />
        </div>
      </div>
    </button>
  );
}
